import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Users, 
  Eye, 
  EyeOff,
  LayoutDashboard,
  ClipboardList,
  UserCircle,
  Hotel,
  CreditCard,
  CalendarClock,
  Bus,
  Mail,
  Settings,
  Loader2
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import { API_URL } from '../../../utils/api';
import WeekenderOverview from './WeekenderOverview';
import WeekenderBookings from './WeekenderBookings';
import WeekenderRooms from './WeekenderRooms';
import WeekenderPayments from './WeekenderPayments';
import WeekenderPaymentPlans from './WeekenderPaymentPlans';
import WeekenderAddOns from './WeekenderAddOns';
import WeekenderCommunications from './WeekenderCommunications';
import WeekenderSettings from './WeekenderSettings';
import WeekenderGuests from './WeekenderGuests';
import BookingDetailDrawer from './BookingDetailDrawer';

const workspaceNavItems = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard, implemented: true },
  { id: 'bookings', label: 'Bookings', icon: ClipboardList, implemented: true },
  { id: 'guests', label: 'Guests', icon: UserCircle, implemented: true },
  { id: 'rooms', label: 'Rooms', icon: Hotel, implemented: true },
  { id: 'payments', label: 'Payments', icon: CreditCard, implemented: true },
  { id: 'payment-plans', label: 'Payment Plans', icon: CalendarClock, implemented: true },
  { id: 'add-ons', label: 'Add-ons', icon: Bus, implemented: true },
  { id: 'communications', label: 'Communications', icon: Mail, implemented: true },
  { id: 'settings', label: 'Settings', icon: Settings, implemented: true },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function WeekenderWorkspace({ 
  event, 
  authToken, 
  onBack,
  onEventUpdated 
}) {
  const [activeSection, setActiveSection] = useState('overview');
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    if (!event?.event_id || !authToken) return;
    
    setSummaryLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/events/${event.event_id}/summary`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [event?.event_id, authToken]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    if (!event?.event_id || !authToken) return;
    
    setBookingsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings?event_id=${event.event_id}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  }, [event?.event_id, authToken]);

  // Load data on mount and when event changes
  useEffect(() => {
    fetchSummary();
    fetchBookings();
  }, [fetchSummary, fetchBookings]);

  // Refresh data after booking changes
  const handleBookingChange = () => {
    fetchSummary();
    fetchBookings();
    if (onEventUpdated) onEventUpdated();
  };

  // Shared state for booking drawer (used by Rooms/Payments)
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openBookingDrawer = (booking) => {
    setSelectedBooking(booking);
    setDrawerOpen(true);
  };

  const closeBookingDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedBooking(null), 300);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <WeekenderOverview 
            event={event}
            summaryData={summaryData}
            loading={summaryLoading}
            onRefresh={fetchSummary}
          />
        );
      case 'bookings':
        return (
          <WeekenderBookings
            event={event}
            bookings={bookings}
            loading={bookingsLoading}
            authToken={authToken}
            onBookingChange={handleBookingChange}
            onRefresh={fetchBookings}
          />
        );
      case 'guests':
        return (
          <WeekenderGuests
            event={event}
            bookings={bookings}
            loading={bookingsLoading}
            authToken={authToken}
            onRefresh={() => { fetchSummary(); fetchBookings(); }}
            onBookingSelect={openBookingDrawer}
          />
        );
      case 'rooms':
        return (
          <WeekenderRooms
            event={event}
            summaryData={summaryData}
            bookings={bookings}
            loading={summaryLoading || bookingsLoading}
            authToken={authToken}
            onRefresh={() => { fetchSummary(); fetchBookings(); }}
            onBookingSelect={openBookingDrawer}
          />
        );
      case 'payments':
        return (
          <WeekenderPayments
            event={event}
            summaryData={summaryData}
            bookings={bookings}
            loading={summaryLoading || bookingsLoading}
            authToken={authToken}
            onRefresh={() => { fetchSummary(); fetchBookings(); }}
            onBookingSelect={openBookingDrawer}
            onRecordPayment={openBookingDrawer}
          />
        );
      case 'payment-plans':
        return (
          <WeekenderPaymentPlans
            event={event}
            bookings={bookings}
            loading={bookingsLoading}
            authToken={authToken}
            onRefresh={() => { fetchSummary(); fetchBookings(); }}
            onBookingSelect={openBookingDrawer}
          />
        );
      case 'add-ons':
        return (
          <WeekenderAddOns
            event={event}
            summaryData={summaryData}
            bookings={bookings}
            loading={summaryLoading || bookingsLoading}
            authToken={authToken}
            onRefresh={() => { fetchSummary(); fetchBookings(); }}
            onBookingSelect={openBookingDrawer}
          />
        );
      case 'communications':
        return (
          <WeekenderCommunications
            event={event}
            bookings={bookings}
            loading={bookingsLoading}
            authToken={authToken}
            onRefresh={() => { fetchSummary(); fetchBookings(); }}
            onBookingSelect={openBookingDrawer}
          />
        );
      case 'settings':
        return (
          <WeekenderSettings
            event={event}
            bookings={bookings}
            authToken={authToken}
            onRefresh={() => { fetchSummary(); fetchBookings(); }}
            onEventUpdated={(updatedEvent) => {
              if (updatedEvent === null) {
                // Event was deleted - go back
                onBack();
              } else {
                // Event was updated - refresh data
                fetchSummary();
                fetchBookings();
              }
            }}
          />
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              {workspaceNavItems.find(n => n.id === activeSection)?.icon && 
                React.createElement(workspaceNavItems.find(n => n.id === activeSection).icon, { className: 'w-8 h-8 text-slate-400' })}
            </div>
            <h3 className="text-lg font-medium text-slate-700">
              {workspaceNavItems.find(n => n.id === activeSection)?.label}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Coming in a future phase
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4"
              onClick={() => setActiveSection('bookings')}
            >
              Go to Bookings
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Workspace Header */}
      <div className="bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          {/* Back + Event Info */}
          <div className="flex items-start gap-4 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex-shrink-0 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Weekenders</span>
            </Button>
            
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900 truncate">
                  {event.title}
                </h1>
                <Badge 
                  variant={event.is_published ? 'default' : 'secondary'}
                  className={cn(
                    "flex-shrink-0",
                    event.is_published 
                      ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" 
                      : "bg-slate-100 text-slate-600"
                  )}
                >
                  {event.is_published ? (
                    <><Eye className="w-3 h-3 mr-1" /> Published</>
                  ) : (
                    <><EyeOff className="w-3 h-3 mr-1" /> Draft</>
                  )}
                </Badge>
              </div>
              
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(event.start_date)} - {formatDate(event.end_date)}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {event.venue_name}
                </span>
                {summaryData && (
                  <span className="flex items-center gap-1 font-medium text-violet-600">
                    <Users className="w-3.5 h-3.5" />
                    {summaryData.total_bookings} bookings, {summaryData.total_guests} guests
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          {summaryData && (
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <div className="text-right">
                <div className="text-slate-500">Received</div>
                <div className="font-semibold text-emerald-600">
                  £{(summaryData.financials?.total_amount_paid || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-slate-500">Outstanding</div>
                <div className="font-semibold text-orange-600">
                  £{(summaryData.financials?.total_balance_due || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Workspace Navigation - Desktop */}
        <nav className="hidden lg:flex flex-col w-56 bg-white border-r border-slate-200 py-4">
          {workspaceNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                disabled={!item.implemented}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                  "hover:bg-slate-50 focus:outline-none focus:bg-slate-100",
                  isActive 
                    ? "bg-violet-50 text-violet-700 border-r-2 border-violet-600" 
                    : "text-slate-600",
                  !item.implemented && "opacity-50 cursor-not-allowed"
                )}
              >
                <Icon className={cn("w-4 h-4", isActive ? "text-violet-600" : "text-slate-400")} />
                <span className="truncate">{item.label}</span>
                {!item.implemented && (
                  <span className="ml-auto text-xs text-slate-400">Soon</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {/* Workspace Navigation - Mobile (sticky horizontal tabs) */}
          <div className="lg:hidden sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2 overflow-x-auto">
            <div className="flex gap-1">
              {workspaceNavItems.filter(item => item.implemented).map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap",
                      isActive 
                        ? "bg-violet-100 text-violet-700" 
                        : "text-slate-600 hover:bg-slate-100"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Main Content */}
          <div className="p-4 lg:p-6">
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Shared Booking Drawer for Rooms/Payments/Add-ons/Communications/Guests sections */}
      {(activeSection === 'rooms' || activeSection === 'payments' || activeSection === 'payment-plans' || activeSection === 'add-ons' || activeSection === 'communications' || activeSection === 'guests') && (
        <BookingDetailDrawer
          booking={selectedBooking}
          event={event}
          open={drawerOpen}
          onClose={closeBookingDrawer}
          authToken={authToken}
          onBookingUpdated={handleBookingChange}
        />
      )}
    </div>
  );
}
